import React, { useEffect, useRef, useState } from "react";
import Table from "rc-table";
import moment from "moment";
import { Button, Pagination, PopupModal } from "../../shared";
import { useCreateTask, useGetTask, useGetTasks } from "../../hooks/task";
import { ConnectorStatus } from "../../types/ReportTypes";
import {
  SubiektTask,
  SubiektTaskDetails,
  SubiektTaskStatus,
  SubiektTaskStockRow,
} from "../../types/SubiektTaskTypes";
import "./TasksPage.scss";

type TasksPageProps = {};

const STATUS_LABELS: {
  [status in SubiektTaskStatus]: { label: string; className: string };
} = {
  pending: { label: "Oczekuje", className: "badge--pending" },
  in_progress: { label: "W trakcie", className: "badge--neutral" },
  done: { label: "Wykonane", className: "badge--ready" },
  failed: { label: "Błąd", className: "badge--cancelled" },
};

const STATUS_FILTERS: { status: SubiektTaskStatus | null; label: string }[] = [
  { status: null, label: "Wszystkie" },
  { status: "pending", label: "Oczekujące" },
  { status: "in_progress", label: "W trakcie" },
  { status: "done", label: "Wykonane" },
  { status: "failed", label: "Z błędem" },
];

const TYPE_LABELS: { [key: string]: string } = {
  PodajStany: "Sprawdzenie stanów",
};

// Tasks a user can add by hand
const TASK_KINDS: { type: "PodajStany"; label: string }[] = [
  { type: "PodajStany", label: "Pobierz stan produktów" },
];

// Codes shown in the list, the rest is in the task details
const CODES_PREVIEW = 3;

// Codes are separated with new lines, commas or semicolons - a code itself may contain a space
const parseCodes = (text: string) =>
  Array.from(
    new Set(
      text
        .split(/[\n\r\t,;]+/)
        .map((code) => code.trim())
        .filter(Boolean)
    )
  );

const formatQuantity = (quantity: number | null) =>
  quantity === null || quantity === undefined
    ? "-"
    : quantity.toLocaleString("pl-PL", { maximumFractionDigits: 3 });

const getConnectorState = (
  connector: ConnectorStatus
): { label: string; className: string; hint: string } => {
  if (!connector.enabled) {
    return {
      label: "Wyłączony",
      className: "badge--neutral",
      hint: "Na serwerze nie ustawiono tokenu konektora (SUBIEKT_CONNECTOR_TOKEN) - zadania nie są tworzone.",
    };
  }
  if (!connector.lastSeenAt) {
    return {
      label: "Nie zgłosił się",
      className: "badge--cancelled",
      hint: "Konektor nie odpytał jeszcze aplikacji - sprawdź jego adres i token.",
    };
  }
  if (!connector.online) {
    return {
      label: "Brak kontaktu",
      className: "badge--cancelled",
      hint: "Konektor przestał odpytywać aplikację - sprawdź, czy komputer z Subiektem jest włączony i ma dostęp do internetu.",
    };
  }
  if (connector.sferaConnected === false) {
    return {
      label: "Brak połączenia z Subiektem",
      className: "badge--cancelled",
      hint: "Konektor odpytuje aplikację, ale nie może połączyć się z Subiektem - zadania czekają.",
    };
  }
  return {
    label: "Połączony",
    className: "badge--ready",
    hint: "Konektor odpytuje aplikację o zadania co około 30 sekund.",
  };
};

const TasksPage: React.FC<TasksPageProps> = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<SubiektTaskStatus | null>(null);
  const [selectedTask, setSelectedTask] = useState<SubiektTask | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [codesText, setCodesText] = useState("");
  const addMenuRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useGetTasks(page, status);
  // Details are read on their own: they carry the result and follow a task that is still open
  const { data: details } = useGetTask(
    selectedTask?.id ?? null,
    isDetailsOpen
  );
  const { mutate: createTask, isPending: isCreating } = useCreateTask({
    onSuccess: (response) => {
      setIsAddOpen(false);
      setCodesText("");
      if (response?.task) {
        setSelectedTask(response.task);
        setIsDetailsOpen(true);
      }
    },
  });

  useEffect(() => {
    if (!isAddMenuOpen) return;

    const closeMenu = (event: MouseEvent) => {
      if (!addMenuRef.current?.contains(event.target as Node)) {
        setIsAddMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [isAddMenuOpen]);

  const formatDate = (date: string | null) =>
    date ? moment(date).local().format("DD.MM.YYYY, HH:mm:ss") : "-";

  const formatDuration = (duration: number | null) =>
    duration === null || duration === undefined
      ? "-"
      : `${(duration / 1000).toLocaleString("pl-PL", {
          maximumFractionDigits: 1,
        })} s`;

  const renderStatus = (record: SubiektTask) => {
    const label = STATUS_LABELS[record.status];
    return (
      <div className="task-status">
        <span className={`badge ${label?.className || ""}`}>
          {label?.label || record.status}
        </span>
        {record.attempts > 0 && record.status !== "done" && (
          <div className="task-status__hint">
            Próba {record.attempts} z {data?.maxAttempts}
          </div>
        )}
        {record.message && (
          <div className="task-status__hint">{record.message}</div>
        )}
      </div>
    );
  };

  const renderScope = (record: SubiektTask) => (
    <div className="task-scope">
      <div>
        {TYPE_LABELS[record.type] || record.type} · produkty:{" "}
        {record.codes.length}
      </div>
      <div className="task-scope__codes">
        {record.codes.slice(0, CODES_PREVIEW).join(", ")}
        {record.codes.length > CODES_PREVIEW &&
          ` i ${record.codes.length - CODES_PREVIEW} więcej`}
      </div>
      {record.manual && (
        <div className="task-scope__codes">
          Dodane ręcznie{record.createdBy ? ` · ${record.createdBy}` : ""}
        </div>
      )}
    </div>
  );

  const renderResultRow = (row: SubiektTaskStockRow) => (
    <tr key={row.code}>
      <td>{row.code}</td>
      {row.found ? (
        <>
          <td>
            <div>{[row.symbol, row.name].filter(Boolean).join(" · ") || "-"}</div>
            {row.warehouses.length > 1 && (
              <div className="task-result__warehouses">
                {row.warehouses
                  .map(
                    (warehouse) =>
                      `${warehouse.symbol}: ${formatQuantity(
                        warehouse.stock
                      )} (dostępne ${formatQuantity(warehouse.available)})`
                  )
                  .join(" · ")}
              </div>
            )}
          </td>
          <td data-label="Stan">{formatQuantity(row.stock)}</td>
          <td data-label="Zarezerwowane">{formatQuantity(row.reserved)}</td>
          <td data-label="Dostępne">{formatQuantity(row.available)}</td>
        </>
      ) : (
        <td
          colSpan={4}
          className={row.error ? "task-result__error" : "task-result__missing"}
        >
          {row.error || "Nie znaleziono produktu w Subiekcie"}
        </td>
      )}
    </tr>
  );

  const renderResult = (task: SubiektTaskDetails) => {
    if (task.status === "pending" || task.status === "in_progress") {
      return (
        <div className="task-details__waiting">
          Zadanie czeka na konektor - wynik pojawi się tutaj sam, zwykle w
          ciągu minuty.
        </div>
      );
    }

    if (!task.result) return null;

    const found = task.result.filter((row) => row.found).length;
    const errors = task.result.filter((row) => row.error).length;

    return (
      <>
        <div className="task-details__label">
          Stany produktów · znalezione: {found}, nieznalezione:{" "}
          {task.result.length - found - errors}
          {errors > 0 && `, z błędem: ${errors}`}
        </div>
        <div className="task-result">
          <table>
            <thead>
              <tr>
                <th>Kod</th>
                <th>Produkt</th>
                <th>Stan</th>
                <th>Zarezerwowane</th>
                <th>Dostępne</th>
              </tr>
            </thead>
            <tbody>{task.result.map(renderResultRow)}</tbody>
          </table>
        </div>
      </>
    );
  };

  const renderDetailsButton = (record: SubiektTask) => (
    <Button
      label="Szczegóły"
      onClick={() => {
        setSelectedTask(record);
        setIsDetailsOpen(true);
      }}
      styleType="stroke"
      size="small"
    />
  );

  const columns = [
    {
      title: "NR",
      key: "id",
      render: (_: any, record: SubiektTask) => record.id,
    },
    {
      title: "ZADANIE",
      key: "scope",
      render: (_: any, record: SubiektTask) => renderScope(record),
    },
    {
      title: "STATUS",
      key: "status",
      render: (_: any, record: SubiektTask) => renderStatus(record),
    },
    {
      title: "UTWORZONE",
      key: "createdAt",
      render: (_: any, record: SubiektTask) => formatDate(record.createdAt),
    },
    {
      title: "POBRANE PRZEZ KONEKTOR",
      key: "issuedAt",
      render: (_: any, record: SubiektTask) => formatDate(record.issuedAt),
    },
    {
      title: "ZAKOŃCZONE",
      key: "finishedAt",
      render: (_: any, record: SubiektTask) => formatDate(record.finishedAt),
    },
    {
      title: "CZAS W SUBIEKCIE",
      key: "durationMs",
      render: (_: any, record: SubiektTask) =>
        formatDuration(record.durationMs),
    },
    {
      title: "",
      key: "actions",
      className: "tasks-table__actions-cell",
      render: (_: any, record: SubiektTask) => renderDetailsButton(record),
    },
  ];

  const mobileColumns = [
    {
      title: "Zadania",
      key: "mobile",
      render: (_: any, record: SubiektTask) => (
        <div className="mobile-record">
          <div className="mobile-record__date">
            Nr {record.id} · {formatDate(record.createdAt)}
          </div>
          {renderScope(record)}
          {renderStatus(record)}
          {renderDetailsButton(record)}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div className="loading">Ładowanie...</div>;
  }

  const connector = data?.connector;
  const connectorState = connector ? getConnectorState(connector) : null;
  const codes = parseCodes(codesText);
  const maxCodes = data?.maxCodes ?? 500;
  // The list row is shown until the details come
  const task: SubiektTaskDetails | null =
    details?.task && details.task.id === selectedTask?.id
      ? details.task
      : selectedTask
        ? { result: null, ...selectedTask }
        : null;

  return (
    <div className="prices-page tasks-page">
      <div className="prices-page__header">
        <h1>Zadania konektora</h1>
        <div className="tasks-page__add" ref={addMenuRef}>
          <Button
            label="Dodaj zadanie"
            onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
            styleType="primary"
            disabled={!connector?.enabled}
          />
          {isAddMenuOpen && (
            <div className="tasks-page__add-menu">
              {TASK_KINDS.map((kind) => (
                <button
                  key={kind.type}
                  type="button"
                  onClick={() => {
                    setIsAddMenuOpen(false);
                    setIsAddOpen(true);
                  }}
                >
                  {kind.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {connector && connectorState && (
        <div className="tasks-connector">
          <div className="tasks-connector__header">
            <h2>Konektor Subiekta</h2>
            <span className={`badge ${connectorState.className}`}>
              {connectorState.label}
            </span>
          </div>
          <p className="tasks-connector__hint">{connectorState.hint}</p>
          {connector.enabled && (
            <dl className="tasks-connector__details">
              <div>
                <dt>Ostatni kontakt</dt>
                <dd>{formatDate(connector.lastSeenAt)}</dd>
              </div>
              <div>
                <dt>Połączenie z Subiektem</dt>
                <dd>
                  {connector.sferaConnected === null
                    ? "-"
                    : !connector.online
                      ? "Nieznane"
                      : connector.sferaConnected
                        ? "Tak"
                        : "Nie"}
                </dd>
              </div>
              <div>
                <dt>Wersja konektora</dt>
                <dd>{connector.version || "-"}</dd>
              </div>
              <div>
                <dt>Produkty czekające na stan</dt>
                <dd>{connector.waitingCodes}</dd>
              </div>
              {connector.lastError && (
                <div className="tasks-connector__error">
                  <dt>Ostatni błąd zgłoszony przez konektor</dt>
                  <dd>{connector.lastError}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      )}

      <div className="tasks-page__filters">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.label}
            label={`${filter.label} (${
              filter.status
                ? data?.counts?.[filter.status] ?? 0
                : Object.values(data?.counts || {}).reduce(
                    (sum, count) => sum + count,
                    0
                  )
            })`}
            onClick={() => {
              setStatus(filter.status);
              setPage(1);
            }}
            styleType={status === filter.status ? "primary" : "stroke"}
            size="small"
          />
        ))}
      </div>

      <Table
        rowKey="id"
        className="prices-table orders-table tasks-table desktop-table"
        data={data?.data || []}
        columns={columns}
        emptyText="Brak zadań do wyświetlenia"
      />

      <Table
        rowKey="id"
        className="prices-table orders-table tasks-table mobile-table"
        data={data?.data || []}
        columns={mobileColumns}
        showHeader={false}
        emptyText="Brak zadań do wyświetlenia"
      />

      {data?.meta && data?.meta?.lastPage > 1 && (
        <Pagination
          totalPages={data?.meta?.lastPage}
          currentPage={data?.meta?.currentPage || page}
          onPageChange={(newPage: number) => setPage(newPage)}
        />
      )}

      <PopupModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)}>
        <div className="popup-modal__header">Pobierz stan produktów</div>
        <div className="task-add">
          <label className="task-add__label" htmlFor="task-codes">
            Kody produktów
          </label>
          <textarea
            id="task-codes"
            className="task-add__codes"
            value={codesText}
            onChange={(event) => setCodesText(event.target.value)}
            placeholder={"5901234567890\nKLOCKI-01"}
            rows={8}
          />
          <div className="task-add__hint">
            Każdy kod w osobnej linii albo po przecinku - EAN lub symbol z
            Subiekta. Wynik zobaczysz w szczegółach zadania, nie zmienia on
            zamówień ani raportów.
          </div>
          <div
            className={`task-add__count ${
              codes.length > maxCodes ? "task-add__count--error" : ""
            }`}
          >
            Rozpoznane kody: {codes.length}
            {codes.length > maxCodes && ` - najwyżej ${maxCodes} w zadaniu`}
          </div>
        </div>
        <div className="popup-modal__buttons">
          <Button
            label="Anuluj"
            onClick={() => setIsAddOpen(false)}
            styleType="stroke"
          />
          <Button
            label="Dodaj zadanie"
            onClick={() => createTask({ type: "PodajStany", codes })}
            styleType="primary"
            disabled={!codes.length || codes.length > maxCodes}
            isLoading={isCreating}
          />
        </div>
      </PopupModal>

      <PopupModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        className={task?.manual ? "task-details-modal" : ""}
      >
        {task && (
          <>
            <div className="popup-modal__header">Zadanie nr {task.id}</div>
            <div className="task-details">
              <div className="task-details__summary">
                {TYPE_LABELS[task.type] || task.type} ·{" "}
                {STATUS_LABELS[task.status]?.label || task.status} · utworzone{" "}
                {formatDate(task.createdAt)}
                {task.manual &&
                  ` · dodane ręcznie${
                    task.createdBy ? ` (${task.createdBy})` : ""
                  }`}
              </div>
              {task.message && (
                <div className="task-details__message">{task.message}</div>
              )}
              {task.manual && renderResult(task)}
              {!(task.manual && task.result) && (
                <>
                  <div className="task-details__label">
                    Kody produktów ({task.codes.length})
                  </div>
                  <div className="task-details__codes">
                    {task.codes.join(", ")}
                  </div>
                </>
              )}
            </div>
            <div className="popup-modal__buttons">
              <Button
                label="Zamknij"
                onClick={() => setIsDetailsOpen(false)}
                styleType="stroke"
              />
            </div>
          </>
        )}
      </PopupModal>
    </div>
  );
};

export default TasksPage;
